// ============================================================
//  MASK SANTA FE — mask-tracker.js
//  Handles: behavioral tracking, visitor profile, scoring
// ============================================================

(function() {
'use strict';

const WORKER_URL  = '/apps/mask-activity-tracker';
const PROFILE_KEY = 'msk_profile';
const SESSION_KEY = 'msk_session';
const SCORE_CACHE = 'msk_scores';
const CACHE_TTL   = 5 * 60 * 1000;

const MaskTracker = {

init() {
this.profile = this._loadProfile();
this.session = this._loadSession();
this._bindEvents();
if (document.querySelector('.grid__item.grid-product[data-id]')) {
this.renderCollectionBadges();
this.renderShippingBadges();
}
this._trackCurrentPage();
},

_bindEvents() {
document.addEventListener('click', (e) => {
const card = e.target.closest('[data-product-id], .grid__item.grid-product');
if (!card) return;
const id = card.dataset.productId || card.dataset.id;
if (id) this.trackClick(id);
});
document.addEventListener('click', (e) => {
const btn = e.target.closest('[data-atc], .js-add-to-cart, [name="add"]');
if (!btn) return;
const form = btn.closest('form[action="/cart/add"]');
if (!form) return;
const id = form.querySelector('[name="product-id"], [data-product-id]')?.value
|| window.__msk_current_product_id;
if (id) this.trackATC(id);
});
document.addEventListener('change', (e) => {
const el = e.target;
if (el.matches('[data-size-filter], .tag--size input, [data-option-name*="ize"]')) {
this._recordSizeInterest(el.value || el.dataset.value);
}
});
this._inferFromURL();
},

_trackCurrentPage() {
const meta = window.__msk_product_meta;
if (!meta) return;
const { id, tags, decade, style } = meta;
this.trackView(id);
if (decade) this._incrementSignal('decades', decade);
if (style)  this._incrementSignal('styles', style);
if (tags) {
tags.filter(t => /^size[-_]?\s*/i.test(t)).forEach(t => {
const sz = t.replace(/^size[-_]?\s*/i, '').trim().toUpperCase();
this._recordSizeInterest(sz);
});
}
this._saveProfile();
},

trackView(productId) {
if (!productId) return;
this._addToHistory('viewed', String(productId));
this._pingWorker(productId, 'view');
},

trackClick(productId) {
if (!productId) return;
this._addToHistory('clicked', String(productId));
this._pingWorker(productId, 'click');
},

trackATC(productId) {
if (!productId) return;
this._addToHistory('atc', String(productId));
this._pingWorker(productId, 'atc');
this._saveProfile();
},

_pingWorker(productId, event) {
if (!WORKER_URL || WORKER_URL.includes('REPLACE')) return;
fetch(`${WORKER_URL}/track`, {
method:  'POST',
headers: { 'Content-Type': 'application/json' },
body:    JSON.stringify({ productId: String(productId), event }),
keepalive: true,
}).catch(() => {});
},

async getScores(productIds) {
if (!productIds?.length) return {};
if (!WORKER_URL || WORKER_URL.includes('REPLACE')) return {};
const cached = this._getCachedScores(productIds);
if (cached) return cached;
try {
const res = await fetch(`${WORKER_URL}/scores?productIds=${productIds.join(',')}`);
if (!res.ok) return {};
const scores = await res.json();
this._cacheScores(scores);
return scores;
} catch { return {}; }
},

async scoreProducts(candidates) {
if (!candidates?.length) return [];
const ids = candidates.map(p => String(p.id));
const crossVisitorScores = await this.getScores(ids);
const profile = this.profile;
return candidates.map(product => {
let score = 0;
const pid = String(product.id);
const cv  = crossVisitorScores[pid] || { views: 0, clicks: 0, atcs: 0, score: 0 };
if (product.sizes?.length && Object.keys(profile.sizes || {}).length) {
const knownSizes = Object.values(profile.sizes).flat().map(s => s.toUpperCase());
if (product.sizes.map(s => s.toUpperCase()).some(s => knownSizes.includes(s))) score += 40;
}
const decadeSignals = profile.decades || {};
const styleSignals  = profile.styles  || {};
if (product.decade && decadeSignals[product.decade]) score += Math.min(30, decadeSignals[product.decade] * 3);
if (product.style  && styleSignals[product.style])   score += Math.min(20, styleSignals[product.style] * 2);
if ((profile.viewed  || []).includes(pid)) score -= 5;
if ((profile.clicked || []).includes(pid)) score += 5;
score += Math.min(25, cv.score / 4);
if (product.inventory === 1) score += 10;
if (product.created_at) {
const daysOld = (Date.now() - new Date(product.created_at)) / 86400000;
if (daysOld < 7) score += 8; else if (daysOld < 30) score += 4;
}
return { productId: pid, product, score: Math.round(score), cv };
}).sort((a, b) => b.score - a.score);
},

getUrgencySignal(productId, inventory, cvData) {
const cv = cvData || {};
const atcs   = cv.atcs   || 0;
const clicks = cv.clicks || 0;
const views  = cv.views  || 0;
if (atcs >= 3)                       return { text: `${atcs} shoppers have this saved`, tier: 'hot' };
if (atcs === 2)                      return { text: '2 people have this in their cart',  tier: 'hot' };
if (atcs === 1 && inventory === 1)   return { text: 'Someone has this in their cart',    tier: 'hot' };
if (clicks >= 20 && inventory === 1) return { text: 'Going fast — No Restocks',          tier: 'urgent' };
if (clicks >= 10)                    return { text: 'High interest today',               tier: 'warm' };
if (views >= 50 && inventory <= 2)   return { text: 'Trending — almost gone',            tier: 'warm' };
if (inventory === 1)                 return { text: 'One of One',                        tier: 'low' };
return null;
},

async renderCollectionBadges() {
const cards = [...document.querySelectorAll('.grid__item.grid-product[data-id]')];
if (!cards.length) return;
const items = cards.map(card => ({
card,
id: card.dataset.id,
inventory: parseInt(card.dataset.qty || card.querySelector('[data-qty]')?.dataset.qty || '0', 10),
}));
const scores = await this.getScores([...new Set(items.map(i => i.id))]);
items.forEach(({ card, id, inventory }) => {
const signal = this.getUrgencySignal(id, inventory, scores[id] || {});
card.querySelector('.msk-urgency-badge')?.remove();
const isSoldOutOrLabeled = card.classList.contains('grid-product__link--disabled') || !!card.querySelector('.grid-product__link--disabled') || !!card.querySelector('.grid-product__tag--sold-out') || !!card.querySelector('.grid-product__tag--custom');
if (isSoldOutOrLabeled || !signal) return;
const badge = document.createElement('div');
badge.className = `msk-urgency-badge msk-badge--${signal.tier}`;
badge.dataset.productId = id;
badge.textContent = signal.text;
const content = card.querySelector('.grid-product__content');
if (content) content.prepend(badge);
});
},

renderShippingBadges() {
document.querySelectorAll('.grid__item.grid-product[data-id]').forEach(card => {
const price = parseFloat(card.querySelector('.grid-product__price')?.textContent?.replace(/[^\d.]/g, '') || '0');
card.querySelector('.msk-shipping-badge')?.remove();
const hasPriorityBadge = card.classList.contains('grid-product__link--disabled')
|| !!card.querySelector('.grid-product__link--disabled')
|| !!card.querySelector('.grid-product__tag--sold-out')
|| !!card.querySelector('.grid-product__tag--custom')
|| !!card.querySelector('.msk-urgency-badge');
if (price >= 100 && !hasPriorityBadge) {
const badge = document.createElement('div');
badge.className = 'msk-shipping-badge';
const content = card.querySelector('.grid-product__content');
if (content) content.prepend(badge);
}
});
},

_recordSizeInterest(size) {
if (!size || size.length > 10) return;
const sz = size.toUpperCase().trim();
if (!this.profile.sizes) this.profile.sizes = {};
const cat = /^\d+/.test(sz) ? 'bottoms' : 'tops';
if (!this.profile.sizes[cat]) this.profile.sizes[cat] = [];
if (!this.profile.sizes[cat].includes(sz)) {
this.profile.sizes[cat].push(sz);
if (this.profile.sizes[cat].length > 10) this.profile.sizes[cat].shift();
}
this._saveProfile();
},

_incrementSignal(type, key) {
if (!key) return;
if (!this.profile[type]) this.profile[type] = {};
this.profile[type][key] = (this.profile[type][key] || 0) + 1;
},

_addToHistory(key, id) {
if (!this.profile[key]) this.profile[key] = [];
this.profile[key] = this.profile[key].filter(i => i !== id);
this.profile[key].unshift(id);
if (this.profile[key].length > 50) this.profile[key].pop();
},

_inferFromURL() {
const path = window.location.pathname;
const decadeMatch = path.match(/1[6-9]\d0s/i);
const styleMap = { 'western':'western','rock':'rock-n-roll','preppy':'preppy','casual':'casual-cool','workwear':'workwear','sport':'sport' };
if (decadeMatch) this._incrementSignal('decades', decadeMatch[0].toLowerCase());
Object.entries(styleMap).forEach(([key, val]) => { if (path.includes(key)) this._incrementSignal('styles', val); });
},

_loadProfile() {
try {
const p = JSON.parse(localStorage.getItem(PROFILE_KEY) || '{}');
p.sizes = p.sizes || {}; p.decades = p.decades || {}; p.styles = p.styles || {};
p.viewed = p.viewed || []; p.clicked = p.clicked || []; p.atc = p.atc || [];
return p;
} catch { return { sizes:{}, decades:{}, styles:{}, viewed:[], clicked:[], atc:[] }; }
},

_saveProfile() {
try { this.profile.lastSeen = Date.now(); localStorage.setItem(PROFILE_KEY, JSON.stringify(this.profile)); } catch {}
},

_loadSession() {
try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}'); } catch { return {}; }
},

_getCachedScores(productIds) {
try {
const cache = JSON.parse(localStorage.getItem(SCORE_CACHE) || '{}');
if (!cache.ts || Date.now() - cache.ts > CACHE_TTL) return null;
return productIds.every(id => cache.data?.[id] !== undefined) ? cache.data : null;
} catch { return null; }
},

_cacheScores(scores) {
try { localStorage.setItem(SCORE_CACHE, JSON.stringify({ ts: Date.now(), data: scores })); } catch {}
},
};

window.MaskTracker = MaskTracker;

if (document.readyState === 'loading') {
document.addEventListener('DOMContentLoaded', () => MaskTracker.init());
} else {
MaskTracker.init();
}

})();
